/**
 * AuthContext.tsx
 * 
 * Current auth model:
 * - Authentication uses Supabase Keycloak with a fixed Teijin IdP hint.
 * - Tenant context comes from core.tenant_memberships.
 * - Styling line access is derived from approved core.user_role_assignments.
 * - Styling role aliases are derived from canonical mod_styling.* permission codes.
 * 
 * IMPORTANT: All queries in the app must include tenant_id filtering!
 * Example: .eq('tenant_id', currentTenantId)
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { extractAccessibleLineIds, fetchApprovedRoleAssignments } from '@/lib/permissions';

const TEIJIN_KEYCLOAK_IDP_HINT = 'tat-cz-microsoft';
// const TEIJIN_KEYCLOAK_IDP_HINT = 'kfs-microsoft';

/**
 * Represents a production line with its full hierarchy path.
 * Used for the line selector dropdown in the UI.
 * 
 * Maps to: mod_styling.v_line_selector view
 * Old equivalent: Site interface
 */
interface Line {
  id: string;
  name: string;
  display_name: string;  // "Factory - Area - Line" combined
  short_display_name: string;  // "Area - Line"
  tenant_id: string;
  factory_id: string;
  factory_name: string;
  area_id: string;
  area_name: string;
}

interface Tenant {
  id: string;
  name: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  
  // Auth methods - NEW
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: AuthError | null }>;
  signInWithMicrosoft: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  
  // Tenant context - NEW
  // IMPORTANT: Use this in all queries: .eq('tenant_id', currentTenantId)
  currentTenantId: string | null;
  setCurrentTenantId: (tenantId: string | null) => void;
  userTenants: Tenant[];
  currentFactoryId: string | null;
  setCurrentFactoryId: (factoryId: string | null) => void;
  currentAreaId: string | null;
  setCurrentAreaId: (areaId: string | null) => void;
  
  // Line access (renamed from site)
  // Migration: currentSiteId → currentLineId
  currentLineId: string | null;
  setCurrentLineId: (lineId: string | null) => void;
  
  // Migration: userSites → userLines
  userLines: Line[];
  
  // All available lines (for showing greyed out inaccessible ones)
  allLines: Line[];
  
  // Migration: accessibleSiteIds → accessibleLineIds
  accessibleLineIds: Set<string>;
  
  // Admin status (now from core.profiles.is_super_admin)
  isSuperAdmin: boolean;
  
  // DEPRECATED - Backward compatibility aliases (remove after full migration)
  /** @deprecated Use currentLineId instead */
  currentSiteId: string | null;
  /** @deprecated Use setCurrentLineId instead */
  setCurrentSiteId: (siteId: string | null) => void;
  /** @deprecated Use userLines instead */
  userSites: Line[];
  /** @deprecated Use allLines instead */
  allSites: Line[];
  /** @deprecated Use accessibleLineIds instead */
  accessibleSiteIds: Set<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Tenant context
  const [currentTenantId, setCurrentTenantIdState] = useState<string | null>(null);
  const [userTenants, setUserTenants] = useState<Tenant[]>([]);
  const [currentFactoryId, setCurrentFactoryIdState] = useState<string | null>(null);
  const [currentAreaId, setCurrentAreaIdState] = useState<string | null>(null);
  
  // Line state (was site state)
  const [currentLineId, setCurrentLineIdState] = useState<string | null>(null);
  const [userLines, setUserLines] = useState<Line[]>([]);
  const [allLines, setAllLines] = useState<Line[]>([]);
  const [accessibleLineIds, setAccessibleLineIds] = useState<Set<string>>(new Set());
  
  // Admin status
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const isFetchingUserDataRef = useRef(false);
  const lastLoadedUserIdRef = useRef<string | null>(null);
  const lastLoadedLinesKeyRef = useRef<string | null>(null);
  const activeLinesLoadKeyRef = useRef<string | null>(null);
  
  const navigate = useNavigate();

  // ===========================================
  // AUTH METHODS
  // ===========================================

  /**
   * Sign in with email and password.
   * After sign in, the onAuthStateChange listener will trigger
   * and load user data including tenant membership.
   */
  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  /**
   * Sign up with email and password.
   * Creates a new user account. The user will need to verify
   * their email (if enabled) and be approved for a tenant.
   */
  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error };
  };

  /**
   * Sign in with Microsoft through Supabase's Keycloak provider.
   * The fixed IdP hint routes Teijin users directly to their Microsoft IdP.
   */
  const signInWithMicrosoft = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'keycloak',
      options: {
        scopes: 'email,profile,openid,fp_tenant_memberships:*',
        redirectTo: `${window.location.origin}/`,
        queryParams: {
          kc_idp_hint: TEIJIN_KEYCLOAK_IDP_HINT,
        },
      },
    });
    return { error };
  };

  /**
   * Sign out the current user.
   * Clears all local state and redirects to auth page.
   */
  const signOut = async () => {
    await supabase.auth.signOut();
    localStorage.removeItem('currentLineId');
    localStorage.removeItem('currentSiteId'); // backward compat
    localStorage.removeItem('currentTenantId');
    localStorage.removeItem('currentFactoryId');
    localStorage.removeItem('currentAreaId');
    setCurrentTenantIdState(null);
    setCurrentFactoryIdState(null);
    setCurrentAreaIdState(null);
    setCurrentLineIdState(null);
    setUserLines([]);
    setAllLines([]);
    setAccessibleLineIds(new Set());
    setIsSuperAdmin(false);
    lastLoadedUserIdRef.current = null;
    lastLoadedLinesKeyRef.current = null;
    activeLinesLoadKeyRef.current = null;
    navigate('/auth');
  };

  // ===========================================
  // LINE SELECTION
  // ===========================================

  /**
   * Set the current working line.
   * This determines which line's data the user sees.
   * Persists to localStorage for session continuity.
   */
  const setCurrentLineId = (lineId: string | null) => {
    setCurrentLineIdState(lineId);

    if (lineId) {
      localStorage.setItem('currentLineId', lineId);
      localStorage.setItem('currentSiteId', lineId); // backward compat
      const selectedLine = allLines.find(line => line.id === lineId);
      if (selectedLine) {
        setCurrentFactoryIdState(selectedLine.factory_id);
        setCurrentAreaIdState(selectedLine.area_id);
        localStorage.setItem('currentFactoryId', selectedLine.factory_id);
        localStorage.setItem('currentAreaId', selectedLine.area_id);
      }
    } else {
      localStorage.removeItem('currentLineId');
      localStorage.removeItem('currentSiteId');
      localStorage.removeItem('currentFactoryId');
      localStorage.removeItem('currentAreaId');
      setCurrentFactoryIdState(null);
      setCurrentAreaIdState(null);
    }
  };

  const setCurrentTenantId = (tenantId: string | null) => {
    setCurrentTenantIdState(tenantId);
    if (tenantId) {
      localStorage.setItem('currentTenantId', tenantId);
    } else {
      localStorage.removeItem('currentTenantId');
    }
  };

  const setCurrentFactoryId = (factoryId: string | null) => {
    setCurrentFactoryIdState(factoryId);
    if (factoryId) {
      localStorage.setItem('currentFactoryId', factoryId);
    } else {
      localStorage.removeItem('currentFactoryId');
    }
  };

  const setCurrentAreaId = (areaId: string | null) => {
    setCurrentAreaIdState(areaId);
    if (areaId) {
      localStorage.setItem('currentAreaId', areaId);
    } else {
      localStorage.removeItem('currentAreaId');
    }
  };

  // ===========================================
  // DATA FETCHING
  // ===========================================

  const loadLinesForTenant = async (
    tenantId: string,
    options?: { isAdmin?: boolean; userId?: string },
  ) => {
    if (!user) return;

    const userId = options?.userId ?? user.id;
    const isAdmin = options?.isAdmin ?? isSuperAdmin;
    const loadKey = `${userId}:${tenantId}:${isAdmin ? 'admin' : 'user'}`;

    if (lastLoadedLinesKeyRef.current === loadKey) return;
    if (activeLinesLoadKeyRef.current === loadKey) return;

    activeLinesLoadKeyRef.current = loadKey;

    try {
      const { data: allLinesData, error: linesError } = await (((supabase as any)
        .schema('mod_styling'))
        .from('v_line_selector')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('display_name') as any);

      if (linesError) {
        console.error('Error fetching lines:', linesError);
      }

      const lines: Line[] = ((allLinesData as any[]) || []).map((l: any) => ({
        id: l.line_id,
        name: l.line_name,
        display_name: l.display_name,
        short_display_name: l.short_display_name,
        tenant_id: l.tenant_id,
        factory_id: l.factory_id,
        factory_name: l.factory_name,
        area_id: l.area_id,
        area_name: l.area_name,
      }));

      if (activeLinesLoadKeyRef.current !== loadKey) return;

      setAllLines(lines);

      let lineIds: string[] = [];

      if (isAdmin) {
        lineIds = lines.map(line => line.id);
      } else {
        try {
          const assignments = await fetchApprovedRoleAssignments(userId, tenantId);
          lineIds = extractAccessibleLineIds(assignments, lines);
        } catch (rolesError) {
          console.error('Error fetching role assignments:', rolesError);
        }
      }

      if (activeLinesLoadKeyRef.current !== loadKey) return;

      const accessibleLines = lines.filter(line => lineIds.includes(line.id));
      setAccessibleLineIds(new Set(lineIds));
      setUserLines(accessibleLines);

      const savedLineId = localStorage.getItem('currentLineId') || localStorage.getItem('currentSiteId');
      const nextLineId = savedLineId && accessibleLines.some(line => line.id === savedLineId)
        ? savedLineId
        : accessibleLines[0]?.id || null;

      setCurrentLineIdState(nextLineId);
      if (nextLineId) {
        localStorage.setItem('currentLineId', nextLineId);
        localStorage.setItem('currentSiteId', nextLineId);
        const selectedLine = lines.find(line => line.id === nextLineId);
        if (selectedLine) {
          setCurrentFactoryIdState(selectedLine.factory_id);
          setCurrentAreaIdState(selectedLine.area_id);
          localStorage.setItem('currentFactoryId', selectedLine.factory_id);
          localStorage.setItem('currentAreaId', selectedLine.area_id);
        }
      } else {
        localStorage.removeItem('currentLineId');
        localStorage.removeItem('currentSiteId');
        localStorage.removeItem('currentFactoryId');
        localStorage.removeItem('currentAreaId');
        setCurrentFactoryIdState(null);
        setCurrentAreaIdState(null);
      }

      lastLoadedLinesKeyRef.current = loadKey;
    } catch (error) {
      console.error('Error fetching lines:', error);
    } finally {
      if (activeLinesLoadKeyRef.current === loadKey) {
        activeLinesLoadKeyRef.current = null;
      }
    }
  };

   /**
    * Fetches the current user's tenant memberships and accessible styling lines.
    * Canonical authorization comes from approved core.user_role_assignments.
    */
  const fetchUserData = async () => {
    if (!user) return;
    if (isFetchingUserDataRef.current || lastLoadedUserIdRef.current === user.id) return;
    isFetchingUserDataRef.current = true;

    try {
      // Step 1: Check if user is super admin
      // Migration: Old checked public.admin_users table
      //           New checks core.profiles.is_super_admin field
      // NOTE: Using 'as any' because types.ts hasn't been regenerated for new schema yet
      const { data: profile, error: profileError } = await (((supabase as any)
        .schema('core'))
        .from('profiles')
        .select('is_super_admin, full_name')
        .eq('id', user.id)
        .maybeSingle() as any);

      if (profileError) {
        console.error('Error fetching profile:', profileError);
      }

      const isAdmin = (profile as any)?.is_super_admin === true;
      setIsSuperAdmin(isAdmin);

      // Step 2: Get user's tenant memberships (or all tenants for super admins)
      // NOTE: Using 'as any' because types.ts hasn't been regenerated for new schema yet
      let tenantOptions: Tenant[] = [];

      if (isAdmin) {
        const { data: tenants, error: tenantError } = await (((supabase as any)
          .schema('core'))
          .from('tenants')
          .select('id, name')
          .eq('is_active', true)
          .order('name') as any);

        if (tenantError) {
          console.error('Error fetching tenants:', tenantError);
        }

        tenantOptions = (tenants as Tenant[]) || [];
      } else {
        const { data: memberships, error: membershipError } = await (((supabase as any)
          .schema('core'))
          .from('tenant_memberships')
          .select('tenant_id, tenants:tenant_id (id, name)')
          .eq('user_id', user.id)
          .eq('status', 'approved') as any);

        if (membershipError) {
          console.error('Error fetching tenant memberships:', membershipError);
        }

        tenantOptions = ((memberships as any[]) || []).map((membership: any) => ({
          id: membership.tenant_id,
          name: membership.tenants?.name || 'Tenant',
        }));
      }

      setUserTenants(tenantOptions);

      const savedTenantId = localStorage.getItem('currentTenantId');
      const tenantId = savedTenantId && tenantOptions.some(t => t.id === savedTenantId)
        ? savedTenantId
        : tenantOptions[0]?.id || null;

      setCurrentTenantId(tenantId);

      if (tenantId) {
        await loadLinesForTenant(tenantId, { isAdmin, userId: user.id });
        lastLoadedUserIdRef.current = user.id;
      } else {
        // No tenant membership - user has no access
        setUserLines([]);
        setAllLines([]);
        setAccessibleLineIds(new Set());
        setCurrentLineIdState(null);
        lastLoadedLinesKeyRef.current = null;
        activeLinesLoadKeyRef.current = null;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      isFetchingUserDataRef.current = false;
    }
  };

  // ===========================================
  // EFFECTS
  // ===========================================

  // Fetch user data when user changes
  useEffect(() => {
    if (user) {
      fetchUserData();
    } else {
      // Clear all state on logout
      setCurrentTenantIdState(null);
      setUserTenants([]);
      setCurrentFactoryIdState(null);
      setCurrentAreaIdState(null);
      setUserLines([]);
      setAllLines([]);
      setAccessibleLineIds(new Set());
      setCurrentLineIdState(null);
      setIsSuperAdmin(false);
      lastLoadedUserIdRef.current = null;
      lastLoadedLinesKeyRef.current = null;
      activeLinesLoadKeyRef.current = null;
      isFetchingUserDataRef.current = false;
    }
  }, [user]);

  useEffect(() => {
    if (!user || !currentTenantId) return;
    loadLinesForTenant(currentTenantId);
  }, [currentTenantId, isSuperAdmin, user]);

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // ===========================================
  // CONTEXT VALUE
  // ===========================================

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      
      // Auth methods
      signIn,
      signUp,
      signInWithMicrosoft,
      signOut,
      
      // Tenant
      currentTenantId,
      setCurrentTenantId,
      userTenants,
      currentFactoryId,
      setCurrentFactoryId,
      currentAreaId,
      setCurrentAreaId,
      
      // Lines (was sites)
      currentLineId,
      setCurrentLineId,
      userLines,
      allLines,
      accessibleLineIds,
      
      // Admin
      isSuperAdmin,
      
      // DEPRECATED backward compatibility aliases
      currentSiteId: currentLineId,
      setCurrentSiteId: setCurrentLineId,
      userSites: userLines,
      allSites: allLines,
      accessibleSiteIds: accessibleLineIds,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
